import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getDistrictUsers } from 'src/shared/services/districtService';
import { Box, Button, Typography } from '@mui/material';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import { OrganizationUserTable } from 'src/shared/components/organisms/organizationUserTable';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getOrganizationDistrictId } from 'src/shared/functions/organizationHelpers';
import { UserCreateModal } from 'src/modules/user/components/userCreateModal';
import { PersonAdd } from '@mui/icons-material';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { DistrictUserResponse } from '../models/districtUserResponse';

export const DistrictUsersPage = () => {
  const { t } = useTranslation(['district']);
  const [loading, setLoading] = useState<boolean>(false);
  const [createMode, setCreateMode] = useState<boolean>(false);
  const [districtUsers, setDistrictUsers] = useState<DistrictUserResponse[]>([]);
  const params = useParams();
  const { setDateStamp, currentUser } = useGlobals();
  const isMounted = useIsMounted();

  const currentDistrictId = getOrganizationDistrictId(currentUser, params.id);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getDistrictUsers(currentDistrictId);
      if (isMounted()) {
        setDistrictUsers(result.data);
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [currentDistrictId, isMounted]);

  useEffect(() => {
    fetchData();
  }, [isMounted, currentDistrictId, fetchData]);

  const handleSave = () => {
    setDateStamp(new Date());
    setCreateMode(false);
    fetchData();
  };

  return (
    <>
      <FormLoaderWrapper loading={loading}>
        <Box sx={{ p: 3, minHeight: '15rem', maxWidth: { md: '100%', lg: '100%', xl: '50%' } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant='h6' gutterBottom>
              {t('common:organization.userPageHeader')}
            </Typography>
            {getPermission(currentUser, PermissionEnum.UserCreateDistrict) && (
              <Button onClick={() => setCreateMode(true)} variant='contained' color='primary' startIcon={<PersonAdd />} disableElevation>
                {t('user:createNewUserButton')}
              </Button>
            )}
          </Box>
          {districtUsers && (
            <OrganizationUserTable<DistrictUserResponse>
              roles={districtUsers}
              idColumn='countyAssUser_id'
              titleColumn='name'
              valueColumn='userName'
              // editCallback={() => ConsoleHelper.log('x')} // Not used
              // deleteCallback={() => ConsoleHelper.log('x')} // Not used
            />
          )}
        </Box>
      </FormLoaderWrapper>
      {createMode && (
        <UserCreateModal
          onClose={() => setCreateMode(false)}
          onSave={() => handleSave()}
          modalOpen={createMode}
          preSelectedDistrict={currentDistrictId ? Number(currentDistrictId) : undefined}
        />
      )}
    </>
  );
};
